import React, { useEffect, useState } from 'react';
import { useResultJSON } from './ResultJSON'; // Assuming this is your custom hook to fetch data
import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { ResponsiveRadar } from '@nivo/radar'; // Importing Radar Chart from Nivo
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface FIPOKTableAndRadarProps {
	section: string;
	title: string;
}

interface FIPOKTableAndRadarData {
	values: number[];
	categories: string[];
}

interface TableRowData {
	ranking: number;
	category: string;
	value: number;
}

/*interface RadarChartDataItem {
	category: string;
	value: number;
}
*/
type RadarChartDataItem = Record<string,any>;

export const FIPOKTableAndRadar: React.FC<FIPOKTableAndRadarProps> = ( { section, title } ) => {
	const jsonData = useResultJSON(); // Assuming this hook fetches JSON data from the backend

	const [tableData, setTableData] = useState<TableRowData[]>([]);
	const [radarChartData, setRadarChartData] = useState<RadarChartDataItem[]>([]);

	useEffect( () =>
		{
			// Assuming that the "jsonData" object has been fetched using your useResultJSON hook
			// and it contains the "section" in the required format
			if ( jsonData == null ) {
				return;
			}

			if ( Object.hasOwn( jsonData, section ) 
				&& Object.hasOwn( jsonData[ section ], "values" ) 
				&& Array.isArray( jsonData[ section ].values ) 
				&& Object.hasOwn( jsonData[ section ], "categories" ) 
				&& Array.isArray( jsonData[ section ].categories ) 
				&& jsonData[ section ].values.length === jsonData[ section ].categories.length ) {
				const apiData = jsonData[ section ] as FIPOKTableAndRadarData;
				const { values, categories } = apiData;
				const tableData: TableRowData[] = values.map( ( value, index ) => (
					{
						ranking: index + 1,
						category: categories[index],
						value,
					} ) );
				setTableData(tableData);
				// Prepare data for Radar Chart
				const convertedRadarChartData = tableData.map( ( data ) => ( { category: data.category, value: data.value } ) );
				setRadarChartData( convertedRadarChartData );
			}
		},
		[
			jsonData,
			section
		] );


	return (
		<Accordion>
			<AccordionSummary expandIcon={<ExpandMoreIcon />}>
				<Typography>{title}</Typography>
			</AccordionSummary>
			<AccordionDetails>
				<div style={{ display: 'flex' }}>
					{/* Table */}
					<Table>
						<TableHead>
							<TableRow>
								<TableCell>#</TableCell>
								<TableCell>Category</TableCell>
								<TableCell>Value</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{ tableData.map( ( row ) => (
							<TableRow key={row.ranking}>
								<TableCell>{row.ranking}</TableCell>
								<TableCell>{row.category}</TableCell>
								<TableCell>{row.value}</TableCell>
							</TableRow>
							) ) }
						</TableBody>
					</Table>

					{/* Radar Chart */}
					<div style={{ height: 600, width: 600, margin: '1rem' }}>
						<ResponsiveRadar
							data={radarChartData}
							keys={['value']}
							indexBy="category"
							maxValue="auto"
							margin={{ top: 70, right: 80, bottom: 40, left: 80 }}
							curve="linearClosed"
							borderWidth={2}
							borderColor={{ from: 'color' }}
							gridLevels={5}
							gridShape="circular"
							gridLabelOffset={15}
							enableDots={true}
							dotSize={8}
							dotColor={{ theme: 'background' }}
							dotBorderWidth={2}
							dotBorderColor={{ from: 'color' }}
							colors={{ scheme: 'category10' }}
							fillOpacity={0.25}
							blendMode="multiply"
							animate={true}
						/>
					</div>
				</div>
			</AccordionDetails>
		</Accordion>
	);
};

export default FIPOKTableAndRadar;
